import { routes } from '../../assets/constants';
import AuthClientStore from '../services/auth-client.service';
import { ApiMethod } from '../models/api-types.type';
import { useAuthContext } from './use-auth-context.hook';

const appendFlowId = (url: string, flowId?: string | null): string => {
  if (!flowId) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}flowId=${encodeURIComponent(flowId)}`;
};

export const useApiIntegrationApi = () => {
  const { sendAuthGuardedRequest } = useAuthContext();

  const getIntegration = async (): Promise<any> => {
    return await sendAuthGuardedRequest(
      ApiMethod.GET,
      routes.apiIntegration.get,
    );
  };

  const saveIntegration = async (data: any): Promise<any> => {
    return await sendAuthGuardedRequest(
      ApiMethod.PUT,
      routes.apiIntegration.save,
      data,
    );
  };

  const deleteIntegration = async (): Promise<void> => {
    await sendAuthGuardedRequest(
      ApiMethod.DELETE,
      routes.apiIntegration.delete,
    );
  };

  const testConnection = async (
    data: any,
  ): Promise<{ success: boolean; missingFields?: string[] }> => {
    const { flowId, ...body } = data;
    return (await sendAuthGuardedRequest(
      ApiMethod.POST,
      appendFlowId(routes.apiIntegration.testConnection, flowId),
      body,
    )) as { success: boolean; missingFields?: string[] };
  };

  const fetchSampleProduct = async (
    data: any,
  ): Promise<{ success: boolean; product?: Record<string, any>; error?: string }> => {
    const { flowId, ...body } = data;
    return (await sendAuthGuardedRequest(
      ApiMethod.POST,
      appendFlowId(routes.apiIntegration.fetchSample, flowId),
      body,
    )) as { success: boolean; product?: Record<string, any>; error?: string };
  };

  const triggerSync = async (flowId?: string): Promise<{ jobId: string }> => {
    return (await sendAuthGuardedRequest(
      ApiMethod.POST,
      appendFlowId(routes.apiIntegration.sync, flowId),
    )) as { jobId: string };
  };

  const getSyncStatus = async (
    jobId: string,
  ): Promise<any> => {
    return await sendAuthGuardedRequest(
      ApiMethod.GET,
      routes.apiIntegration.syncStatus(jobId),
    );
  };

  const getLatestSync = async (): Promise<any> => {
    return await sendAuthGuardedRequest(
      ApiMethod.GET,
      routes.apiIntegration.latestSync,
    );
  };

  const runAiMapping = (
    params: { connectorType: string; connectorConfig?: Record<string, any>; flowId?: string },
    onEvent: (event: { type: string; data: any }) => void,
    onDone: () => void,
    onError: (err: string) => void,
  ): AbortController => {
    const apiUrl = process.env.REACT_APP_PMS_API_URL as string;
    const token = AuthClientStore.getAccessToken();
    const controller = new AbortController();

    const { flowId, ...body } = params;
    const url = appendFlowId(`${apiUrl}${routes.apiIntegration.aiMap}`, flowId);

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    }).then(async (response) => {
      if (!response.ok) {
        onError(`HTTP ${response.status}: ${response.statusText}`);
        return;
      }
      const reader = response.body?.getReader();
      if (!reader) { onError('No response body'); return; }
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data: ')) continue;
          const payload = trimmed.slice(6);
          if (payload === '[DONE]') { onDone(); return; }
          try {
            onEvent(JSON.parse(payload));
          } catch { /* skip malformed */ }
        }
      }
      onDone();
    }).catch((err) => {
      if (err.name !== 'AbortError') onError(err.message);
    });

    return controller;
  };

  return {
    getIntegration,
    saveIntegration,
    deleteIntegration,
    testConnection,
    fetchSampleProduct,
    triggerSync,
    getSyncStatus,
    getLatestSync,
    runAiMapping,
  };
};
